import { CURRENCY_USD, SEARCH_TYPE_TRIP } from './constants';
import {
    collapseTripItems,
    compact,
    resolveItemListName,
    rollUpMatchType,
    sumItemsValue,
} from './items';
import { buildSearchTerm } from './search';
import { gtm } from '../gtm';

import type { SearchedCity } from './search';
import type { EcommerceItem } from './types';

/**
 * The one way this app writes a GA4 ecommerce event.
 *
 * Every push is preceded by `{ ecommerce: null }`: GTM's dataLayer merges
 * rather than replaces, so without the reset an event inherits whatever items
 * the previous one left behind. Same pattern the legacy checkout's `purchase`
 * has used since it shipped (Checkout.jsx `sendBookingCompleteEvent`).
 */
function pushEcommerceEvent(event: string, ecommerce: Record<string, unknown>): void {
    gtm.pushToDataLayer({ ecommerce: null });
    // Compacted for the same reason the items are: a parameter the surface
    // could not supply is absent, not present-and-empty.
    gtm.pushToDataLayer({ ecommerce: compact(ecommerce), event });
}

/**
 * The currency the items' `price` fields are denominated in, for the two list
 * events. They carry no event-level `value` -- a list is not a basket, and
 * Google's own reference omits it -- but their items are priced, and the
 * contract puts all money in USD, so the denomination is stated rather than
 * left to be inferred. Omitted when nothing in the list has a price.
 */
function listCurrency(items: EcommerceItem[]): string | undefined {
    return items.some((item) => typeof item.price === 'number')
        ? CURRENCY_USD
        : undefined;
}

/**
 * Results rendered for one leg. Collapsed to one item per route × mode, so the
 * item count is the number of distinct routes on screen, not the number of
 * trips -- `quantity` carries that.
 */
function viewItemList({
    itemListId,
    items,
}: {
    itemListId?: string;
    items: EcommerceItem[];
}): void {
    if (!items.length) {
        return;
    }

    const collapsed = collapseTripItems(items);

    pushEcommerceEvent('view_item_list', {
        currency: listCurrency(collapsed),
        item_list_id: itemListId,
        item_list_name: resolveItemListName(itemListId),
        // Rolled up over the trips, not the collapsed items: a route with one
        // direct and one nearby trip is `mixed`, which the collapsed item alone
        // could not say.
        match_type: rollUpMatchType(items),
        items: collapsed,
    });
}

function selectItem({
    item,
    itemListId,
}: {
    item: EcommerceItem;
    itemListId?: string;
}): void {
    pushEcommerceEvent('select_item', {
        currency: listCurrency([item]),
        item_list_id: itemListId,
        item_list_name: resolveItemListName(itemListId),
        items: [item],
    });
}

function cartEvent(
    event: string,
    items: EcommerceItem[],
    extras: Record<string, unknown> = {},
): void {
    if (!items.length) {
        return;
    }

    const value = sumItemsValue(items);

    pushEcommerceEvent(event, {
        // Currency and value travel together or not at all. An event with a
        // currency and no amount reads as a free basket downstream; a trip
        // whose pricing has not resolved simply has no money on it yet.
        ...(value === undefined
            ? {}
            : { currency: CURRENCY_USD, value }),
        ...extras,
        items,
    });
}

/**
 * Fires only once a livecheck has resolved clean, at the live price. GA4
 * semantics put the items *added* in `value` here -- basket size is read off
 * the cart events instead.
 *
 * Carries the `match_type` rollup as well as the per-item value: this is the
 * intent signal the Ads audiences segment on, and event-scoped is the only
 * scope those audiences can read.
 */
function addToCart({ items }: { items: EcommerceItem[] }): void {
    cartEvent('add_to_cart', items, { match_type: rollUpMatchType(items) });
}

function removeFromCart({ items }: { items: EcommerceItem[] }): void {
    cartEvent('remove_from_cart', items);
}

/** The whole surviving basket, after the Trip Summary re-livecheck resolves. */
function viewCart({ items }: { items: EcommerceItem[] }): void {
    cartEvent('view_cart', items);
}

/**
 * `checkoutDestination` splits a partner deep-link click, which hands the
 * traveler to the carrier's own site, from a Wanderu checkout. Left off when the
 * caller has no way to tell rather than guessed at.
 */
function beginCheckout({
    checkoutDestination,
    items,
}: {
    checkoutDestination?: string;
    items: EcommerceItem[];
}): void {
    cartEvent('begin_checkout', items, checkoutDestination
        ? { checkout_destination: checkoutDestination }
        : {});
}

/**
 * One committed trip search (ui-react#1222). GA4's recommended `search`, which
 * sGTM forwards to TikTok as `Search`.
 *
 * Top-level parameters with no `ecommerce` object: there are no items at search
 * time -- `view_item_list` describes what came back -- and GA4 drops `items` on
 * a non-ecommerce event anyway. The `{ ecommerce: null }` reset is still pushed
 * first: GTM's data layer model keeps the last `ecommerce` object and the GA4
 * forwarder reads `ecommerce.*` on every event it fires on, and `search` can
 * land after a `view_item_list` or a `view_cart`. Names mirror Amplitude's
 * `Search Submitted`. The term names the cities the picks lie in, and nothing
 * the traveler typed is a parameter here at all.
 *
 * A round trip is one search, not one per leg.
 */
function search({
    arriveCity,
    departCity,
    entryPoint,
    outboundDate,
    paxCount,
    returnDate,
}: {
    arriveCity?: SearchedCity;
    departCity?: SearchedCity;
    entryPoint: string;
    outboundDate?: string;
    paxCount: number;
    returnDate?: string;
}): void {
    gtm.pushToDataLayer({ ecommerce: null });
    gtm.pushToDataLayer(compact({
        entry_point: entryPoint,
        event: 'search',
        is_one_way: !returnDate,
        outbound_date_searched: outboundDate,
        pax_count: paxCount,
        return_date_searched: returnDate || undefined,
        search_term: buildSearchTerm(departCity, arriveCity),
        search_type: SEARCH_TYPE_TRIP,
    }));
}

export {
    addToCart,
    beginCheckout,
    pushEcommerceEvent,
    removeFromCart,
    search,
    selectItem,
    viewCart,
    viewItemList,
};
